import { useActivityTemplateType } from "PFApp/activities/hooks";
import { BookingDetailItem } from "PFApp/booking/components/booking_detail_item";
import { DetailsPanelSource } from "PFApp/booking/components/details_panel";
import { BookingDetailItemWorkforceMember } from "PFApp/booking/parts/overview/detail_view/booking_detail/booking_detail_content/booking_detail_item_workforce_member";
import { LoadingDots } from "PFComponents/loading_dots";
import { useIsExternalUsage } from "PFCore/hooks/queries/bookings/use_is_external_usage";
import { Activity, Booking } from "PFTypes";
import { Dispatch, SetStateAction } from "react";
import { useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import useBookingModulePermissions from "../../../../use_booking_module_permissions";
import {
  BookingFormData,
  BookingFormDataCreateMode,
  BookingFormDataEditOrCloneMode,
  BookingFormMode
} from "../booking_form.types";
import { isEditMode } from "../booking_form.utils";
import { BookingFormBasicProfile, BookingFormValues } from "../booking_form_values.types";
import { useInitialWorkforceMember } from "./use_initial_workforce_member";
import { WorkforceMemberSelectDropdown } from "./workforce_member_select_dropdown";

type WorkforceMemberSelectProps = {
  initialData: BookingFormData;
  mode: BookingFormMode;
  booking?: Booking;
  source?: DetailsPanelSource;
  loadingState: Record<string, boolean>;
  setLoadingState: Dispatch<SetStateAction<Record<string, boolean>>>;
  initialActivity?: Activity;
};

export const WorkforceMemberSelect = ({
  initialData,
  mode,
  booking,
  source,
  loadingState,
  setLoadingState,
  initialActivity
}: WorkforceMemberSelectProps) => {
  const { t } = useTranslation("bookingModule");
  const { isPermittedToEdit: isPermittedToEditBookings } = useBookingModulePermissions();
  const isExternal = useIsExternalUsage();

  const profileId =
    mode === BookingFormMode.Create
      ? (initialData as BookingFormDataCreateMode).profileId
      : booking?.profile_id;
  const suspendedProfileDetails = isEditMode(mode)
    ? (initialData as BookingFormDataEditOrCloneMode).suspendedProfileDetails
    : undefined;
  const { loading } = useInitialWorkforceMember({
    profileId,
    suspendedProfileDetails,
    loadingState,
    setLoadingState
  });

  const workforceMember = useWatch<BookingFormValues, "workforceMember">({ name: "workforceMember" });
  const { isEngagement } = useActivityTemplateType(initialActivity);

  const isSelectWorkforceMemberEnabled =
    !isExternal &&
    mode === BookingFormMode.Create &&
    (!initialActivity || isEngagement) &&
    isPermittedToEditBookings;

  if (!isSelectWorkforceMemberEnabled) {
    return (
      <BookingDetailItemWorkforceMember
        profile={loading ? undefined : (workforceMember as BookingFormBasicProfile | undefined)}
        source={source}
        activity={initialActivity}
      />
    );
  }

  if (loading) {
    return <BookingDetailItem label={t("bookings.create.workforceMember")} value={<LoadingDots />} />;
  }

  return <WorkforceMemberSelectDropdown initialData={initialData} mode={mode} />;
};
